import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Search, Upload, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DataTable from "@/components/DataTable";

export default function Patients() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // patient being edited (admin only)
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async (query = "") => {
    try {
      const { data } = await api.get("/patients", { params: query ? { q: query } : {} });
      setPatients(data);
    } catch (err) {
      console.error("Failed to load patients:", err);
      toast.error("Failed to load patients");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const bulkDelete = async (ids) => {
    try {
      const { data } = await api.post("/patients/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} patient(s)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk delete failed");
    }
  };

  const columns = useMemo(() => ([
    {
      key: "name", label: "Patient", sortable: true,
      accessor: (p) => p.name,
      render: (p) => (
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-[color:var(--primary-light)] grid place-items-center text-[color:var(--primary-hover)] text-xs font-medium">{p.name?.[0]}</span>
          <div>
            <Link to={`/patients/${p.id}`} className="font-medium text-[color:var(--ink)] hover:text-[color:var(--primary)]" data-testid={`patient-view-${p.id}`}>{p.name}</Link>
            <p className="text-xs text-[color:var(--muted)]">
              {p.gender || "—"} · {p.dob ? `${new Date().getFullYear() - new Date(p.dob).getFullYear()} yrs` : "—"}
            </p>
          </div>
        </div>
      ),
    },
    { key: "phone", label: "Phone", sortable: true, accessor: (p) => p.phone || "" },
    { key: "email", label: "Email", sortable: true, accessor: (p) => p.email || "", defaultHidden: true },
    { key: "occupation", label: "Occupation", sortable: true, accessor: (p) => p.occupation || "", defaultHidden: true },
    {
      key: "tags", label: "Tags",
      render: (p) => (
        <div className="flex flex-wrap gap-1">
          {(p.chronic_tags || []).map((t, i) => (
            <span key={i} className="text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full bg-[color:var(--primary-light)]/40 text-[color:var(--primary-hover)]">{t}</span>
          ))}
        </div>
      ),
    },
    {
      key: "created_at", label: "Added", sortable: true, defaultHidden: true,
      accessor: (p) => p.created_at,
      render: (p) => p.created_at ? new Date(p.created_at).toLocaleDateString() : "—",
    },
  ]), []);

  return (
    <div data-testid="patients-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[color:var(--muted)]">Patients</p>
          <h1 className="font-heading text-3xl md:text-4xl text-[color:var(--ink)] font-medium mt-1">All patients</h1>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <BulkUploadDialog open={bulkOpen} setOpen={setBulkOpen} onDone={load} />
          <PatientDialog open={createOpen} setOpen={setCreateOpen} onSaved={load} />
        </div>
      </div>

      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[color:var(--muted)]" />
        <Input value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} placeholder="Search by name, phone or email" className="pl-9" data-testid="patients-search" />
      </div>

      <DataTable
        storageKey="patients"
        rows={patients}
        columns={columns}
        isAdmin={isAdmin}
        onEdit={isAdmin ? setEditing : undefined}
        onDeleteMany={isAdmin ? bulkDelete : undefined}
        emptyMessage="No patients yet."
      />

      {editing && (
        <PatientDialog
          open={!!editing}
          setOpen={(v) => { if (!v) setEditing(null); }}
          existing={editing}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function PatientDialog({ open, setOpen, onSaved, existing }) {
  const isEdit = !!existing;
  const empty = { name: "", phone: "", email: "", gender: "", dob: "", address: "", occupation: "", medical_history: "", allergies: "", current_medications: "", chronic_tags: [] };
  const [form, setForm] = useState(empty);
  const [tagsInput, setTagsInput] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (existing) {
      setForm({ ...empty, ...existing });
      setTagsInput((existing.chronic_tags || []).join(", "));
    } else {
      setForm(empty);
      setTagsInput("");
    }
  }, [existing, open]);

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    const payload = {
      ...form,
      chronic_tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
    };
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/patients/${existing.id}`, payload);
        toast.success("Patient updated");
      } else {
        await api.post("/patients", payload);
        toast.success("Patient added");
      }
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isEdit && (
        <DialogTrigger asChild>
          <Button className="rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="add-patient-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New patient
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="patient-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit patient" : "Add new patient"}</DialogTitle>
          <DialogDescription className="text-[color:var(--muted)]">
            {isEdit ? "Update any field — changes apply immediately." : "Quickly add a new patient. Only name is required."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4 mt-2">
          {[
            ["name", "Full name *", "text"],
            ["phone", "Phone", "text"],
            ["email", "Email", "email"],
            ["gender", "Gender", "text"],
            ["dob", "Date of birth", "date"],
            ["occupation", "Occupation", "text"],
          ].map(([k, l, t]) => (
            <div key={k}>
              <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">{l}</label>
              <Input type={t} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" data-testid={`patient-${k}`} />
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Address</label>
            <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" data-testid="patient-address" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Chronic tags (comma separated)</label>
            <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} className="mt-1.5" placeholder="Migraine, Eczema" data-testid="patient-tags" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Medical history</label>
            <Input value={form.medical_history || ""} onChange={(e) => setForm({ ...form, medical_history: e.target.value })} className="mt-1.5" data-testid="patient-history" />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Allergies</label>
            <Input value={form.allergies || ""} onChange={(e) => setForm({ ...form, allergies: e.target.value })} className="mt-1.5" data-testid="patient-allergies" />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Current medications</label>
            <Input value={form.current_medications || ""} onChange={(e) => setForm({ ...form, current_medications: e.target.value })} className="mt-1.5" data-testid="patient-medications" />
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="patient-cancel">Cancel</Button>
          <Button onClick={save} disabled={loading || !form.name} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="patient-save">{loading ? "Saving…" : isEdit ? "Save changes" : "Save patient"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkUploadDialog({ open, setOpen, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setResult(null); if (fileRef.current) fileRef.current.value = ""; };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/patients/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "patients_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Could not download template");
    }
  };

  const submit = async () => {
    if (!file) { toast.error("Please pick a CSV file"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/patients/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} patient(s)${data.skipped ? `, skipped ${data.skipped}` : ""}`);
        onDone();
      } else {
        toast.warning("No patients imported. Check the row errors.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full border-[color:var(--secondary)] text-[color:var(--secondary)] hover:bg-[color:var(--secondary-light)]/30 hover:text-[color:var(--secondary)]" data-testid="bulk-upload-btn">
          <Upload className="w-4 h-4 mr-1.5" /> Bulk upload
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Bulk import patients (CSV)</DialogTitle>
          <DialogDescription className="text-[color:var(--muted)]">
            Upload a CSV with one patient per row. Only <strong className="text-[color:var(--ink)]">name</strong> is required; all other columns are optional.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-[color:var(--bg-marketing)] border border-[color:var(--border)] rounded-xl p-4 mt-2">
          <p className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)] mb-2">Expected columns</p>
          <code className="block text-[11px] text-[color:var(--ink)] leading-relaxed bg-white border border-[color:var(--border)] rounded-md px-3 py-2 overflow-x-auto whitespace-nowrap">
            name, dob, gender, phone, email, address, occupation, medical_history, allergies, current_medications, chronic_tags
          </code>
          <p className="text-[11px] text-[color:var(--muted)] mt-2 leading-relaxed">
            <strong>dob</strong> format <code>YYYY-MM-DD</code> · <strong>chronic_tags</strong> semicolon-separated · UTF-8 encoded.
          </p>
          <Button variant="outline" size="sm" onClick={downloadTemplate} className="mt-3 rounded-full text-xs h-8" data-testid="download-template-btn">
            <FileDown className="w-3.5 h-3.5 mr-1" /> Download sample CSV
          </Button>
        </div>

        <div className="mt-4">
          <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">CSV file</label>
          <Input ref={fileRef} type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1.5 cursor-pointer" data-testid="bulk-file-input" />
          {file && <p className="text-xs text-[color:var(--muted)] mt-1.5">Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
        </div>

        {result && (
          <div className="mt-3 bg-white border border-[color:var(--border)] rounded-xl p-4" data-testid="bulk-result">
            <p className="text-sm"><span className="font-semibold text-[color:var(--success)]">{result.created} imported</span>
              {result.skipped > 0 && <span className="text-[color:var(--muted)]">  ·  {result.skipped} skipped</span>}
            </p>
            {result.errors?.length > 0 && (
              <ul className="mt-2 max-h-32 overflow-y-auto text-xs space-y-1">
                {result.errors.map((e, idx) => (<li key={idx} className="text-[color:var(--error)]">Row {e.row}: {e.error}</li>))}
              </ul>
            )}
          </div>
        )}

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="bulk-cancel">Close</Button>
          <Button onClick={submit} disabled={loading || !file} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="bulk-submit">
            {loading ? "Importing…" : "Import patients"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
